import React, { FC } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvTable from '../UI/molecules/OvTable';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { ApiKeyDataUtils } from '../../common/utils/services/api-key-data-utils';
import { useAdminApiKey } from '../../hooks/use-admin-api-key';
import { useAdminApiKeyMutations } from '../../hooks/use-admin-api-key-mutations';
import { API_KEY_STATUS_TONES } from '../../hooks/use-api-key-list-columns';

const API_KEY_LIST_PATH = '/api-keys';

const ApiKeyDetails: FC = () => {
  const { apiKeyId } = useParams();
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const apiKeyOperations = useAuthorized(Resource.ApiKey);
  const apiKeyUpdateAllowed =
    apiKeyOperations.update || applicationOperations.supervise;

  const {
    data: apiKey,
    isFetching,
    error: apiKeyError,
    refetch,
  } = useAdminApiKey(apiKeyId);
  const { updateApiKey } = useAdminApiKeyMutations();

  if (!apiKey) {
    return (
      <OvRecordDetailsPage>
        {apiKeyError ? (
          <OvRecordDetailsErrorState
            title={t('apiKeys.details.errorTitle')}
            error={apiKeyError}
            onRetry={() => refetch()}
            backLabel={t('common.apiKeys')}
            backPath={API_KEY_LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const error = apiKeyError ?? updateApiKey.error;
  const isLoading = isFetching || updateApiKey.isPending;
  const apiKeyDisplayId = apiKey.id as string;

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('apiKeys.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('common.apiKeys')}
              fallbackPath={API_KEY_LIST_PATH}
            />
          }
          current={apiKey.partner_name}
          title={apiKey.partner_name}
          titleAside={
            <OvStatusChip
              label={t(`apiKeys.status.${apiKey.status}`)}
              tone={API_KEY_STATUS_TONES[apiKey.status]}
            />
          }
          meta={
            <>
              <span>
                {apiKeyDisplayId}
                <OvCopyToClipboardButton
                  text={apiKeyDisplayId}
                  label={t('apiKeys.details.copyId')}
                />
              </span>
              <span>{apiKey.partner_route}</span>
              {apiKey.created_at && (
                <span>
                  <OvDateParser
                    date={apiKey.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            apiKeyUpdateAllowed && (
              <OvRecordHeaderActions>
                <NavLink to={`/api-keys/edit/${apiKey.id}`}>
                  <OvCompactButton icon={<EditIcon />}>
                    {t('common.actions.edit')}
                  </OvCompactButton>
                </NavLink>
              </OvRecordHeaderActions>
            )
          }
        />

        <OvRecordDetailsSection title={t('apiKeys.details.section')}>
          <OvTable
            data={ApiKeyDataUtils.mapDataForApiKeyDetailsTable(apiKey)}
            theme="Light"
            onSaveRowValue={(updatedProperties) =>
              updateApiKey.mutate({
                id: apiKeyDisplayId,
                apiKey: updatedProperties,
              })
            }
          />
        </OvRecordDetailsSection>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default ApiKeyDetails;
